import {
  BodyText,
  Box,
  Card,
  Container,
  Flex,
  Heading,
} from '@utilitywarehouse/hearth-react';
import {
  CashbackCardMediumIcon,
  CelebrationMediumIcon,
} from '@utilitywarehouse/hearth-react-icons';
import { semantic } from '@utilitywarehouse/hearth-tokens/browser';
import { useCallback, useEffect, useState } from 'react';

import { CarouselControls } from '@components/CarouselControls';

import './styles.scss';
import app1 from '/cashback/app-1.jpg';
import app2 from '/cashback/app-2.jpg';
import app3 from '/cashback/app-3.jpg';
import app4 from '/cashback/app-4.jpg';

type PillColorScheme =
  | 'brand'
  | 'mobile'
  | 'energy'
  | 'broadband'
  | 'insurance'
  | 'cashback'
  | 'pig';

interface Testimonial {
  id: number;
  image: string;
  quote: string;
  colorScheme: PillColorScheme;
}

const QUOTE_LEFT = ['39%', '50%', '60%', '58%'];

const DISTANCE_TILT_DIR = ['pos', 'pos', 'pos', 'neg'] as const;

const testimonials: Testimonial[] = [
  {
    id: 1,
    image: app1,
    quote: "Richard's earning £481.50 and counting!",
    colorScheme: 'broadband',
  },
  {
    id: 2,
    image: app2,
    quote: 'Louise has earned £623.72 so far!',
    colorScheme: 'insurance',
  },
  {
    id: 3,
    image: app3,
    quote: 'Ibrahim has earned £614.53 already!',
    colorScheme: 'pig',
  },
  {
    id: 4,
    image: app4,
    quote: 'Dan earned £1,216.62 to date!',
    colorScheme: 'energy',
  },
];

const AUTOPLAY_DURATION = 7000;

const useCardStack = (itemCount: number, duration = AUTOPLAY_DURATION) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const goTo = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const next = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % itemCount);
  }, [itemCount]);

  const prev = useCallback(() => {
    setCurrentIndex((prevIdx) => (prevIdx - 1 + itemCount) % itemCount);
  }, [itemCount]);

  useEffect(() => {
    if (isHovered) return;

    const interval = setInterval(() => {
      setCurrentIndex((current) => (current + 1) % itemCount);
    }, duration);

    return () => clearInterval(interval);
  }, [isHovered, duration, itemCount, currentIndex]);

  const getDistance = useCallback(
    (index: number) => (index - currentIndex + itemCount) % itemCount,
    [currentIndex, itemCount]
  );

  return {
    currentIndex,
    goTo,
    next,
    prev,
    pause: () => setIsHovered(true),
    resume: () => setIsHovered(false),
    getDistance,
  };
};

interface TestimonialStackProps {
  currentIndex: number;
  getDistance: (index: number) => number;
  next: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const TestimonialStack = ({
  currentIndex,
  getDistance,
  next,
  onMouseEnter,
  onMouseLeave,
}: TestimonialStackProps) => {
  return (
    <Flex
      direction="column"
      alignItems="center"
      className="cbc-testimonials__stack-column"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Box position="relative" className="cbc-testimonials__stack">
        {testimonials.map((testimonial, i) => {
          const distance = getDistance(i);
          const isTop = distance === 0;
          const isOuterCard = i === 0 || i === testimonials.length - 1;
          const tilt = isOuterCard ? -2.5 : 2.5;

          return (
            <Card
              key={testimonial.id}
              variant="emphasis"
              colorScheme="neutralStrong"
              paddingNone
              className={`cbc-testimonials__stack-card${
                isTop ? ' cbc-testimonials__stack-card--top' : ''
              }`}
              data-tilt={tilt}
              data-distance={distance}
              data-tilt-dir={DISTANCE_TILT_DIR[distance]}
              aria-hidden={!isTop}
              onClick={isTop ? next : undefined}
            >
              <img
                src={testimonial.image}
                alt=""
                className="cbc-testimonials__image"
              />

              <Box
                position="absolute"
                top="68%"
                width="65%"
                left={QUOTE_LEFT[i]}
                className="cbc-testimonials__quote-pin"
                data-position={i}
              >
                <Card
                  variant="subtle"
                  colorScheme={testimonial.colorScheme}
                  shadowColor={testimonial.colorScheme}
                  direction="row"
                  spacing="sm"
                  alignItems="center"
                  paddingNone
                >
                  <Flex padding="150" gap="100">
                    <CelebrationMediumIcon />
                    <Heading asChild size="sm">
                      <p>{testimonial.quote}</p>
                    </Heading>
                  </Flex>
                </Card>
              </Box>
            </Card>
          );
        })}

        <Box aria-live="polite" className="cbc-testimonials__sr-only">
          {testimonials[currentIndex].quote}
        </Box>
      </Box>
    </Flex>
  );
};

const DontTakeOurWordForIt = () => {
  const { currentIndex, goTo, next, prev, pause, resume, getDistance } =
    useCardStack(testimonials.length);

  return (
    <Container
      paddingY={{ mobile: '350', desktop: '500' }}
      backgroundColor={semantic.surface.energy.subtle}
    >
      <Flex spacing="lg" className="cbc-testimonials wrapper">
        <Flex className="cbc-testimonials__row">
          <Flex className="cbc-testimonials__header">
            <CashbackCardMediumIcon />
            <Heading
              as="h2"
              size="2xl"
              textAlign="center"
              className="cbc-testimonials__title"
            >
              Don&rsquo;t take
              <br />
              our word for it!
            </Heading>
            <BodyText size="lg" color="secondary" textAlign="center">
              See how much other UW members saved
            </BodyText>
          </Flex>

          <TestimonialStack
            currentIndex={currentIndex}
            getDistance={getDistance}
            next={next}
            onMouseEnter={pause}
            onMouseLeave={resume}
          />
        </Flex>

        <CarouselControls
          count={testimonials.length}
          activeIndex={currentIndex}
          onSelect={goTo}
          onPrev={prev}
          onNext={next}
          itemLabel="card"
          className="cbc-testimonials__controls"
        />
      </Flex>
    </Container>
  );
};

export { DontTakeOurWordForIt };
